import React, { useEffect, useState } from 'react';
import { HeartPulse, ArrowRight } from 'lucide-react';
import { getHospitalsNearby } from '@/lib/api';

function formatDistance(m) {
  if (!m && m !== 0) return '';
  return m < 1000 ? `${m}m` : `${(m / 1000).toFixed(1)}km`;
}

export default function HospitalsCard({ lat, lon }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (typeof lat !== 'number' || typeof lon !== 'number') { setLoading(false); return; }
    let alive = true;
    setLoading(true);
    getHospitalsNearby(lat, lon, 5)
      .then(r => { if (alive) setItems((r?.places || []).slice(0, 4)); })
      .catch(() => { if (alive) setItems([]); })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [lat, lon]);

  if (loading) return <div className="puvio-card p-5 gt-skeleton h-28" data-testid="hospitals-card-skeleton" />;
  if (!items.length) return null;

  return (
    <div className="puvio-card p-5" data-testid="hospitals-card">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-xl bg-[#FDECEC] border border-[#F5B0B0]/60 flex items-center justify-center">
          <HeartPulse className="w-4 h-4 text-[#B91C2E]" />
        </div>
        <div>
          <p className="font-bold text-[10.5px] uppercase tracking-[0.22em] text-[#15291C]">Hospitals nearby</p>
          <p className="text-[12px] text-[#7A8A80] -mt-0.5">Just in case</p>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        {items.map((h, i) => {
          const href = `https://www.google.com/maps/search/?api=1&query=${h.lat || lat},${h.lon || lon}&query_place_id=${h.id || ''}`;
          return (
            <a
              key={h.id || i}
              href={href}
              target="_blank" rel="noreferrer"
              className="flex items-center gap-3 rounded-2xl px-3 py-2.5 hover:bg-[#0F1A13]/04 transition-colors border border-[#0F1A13]/05 gt-focus"
              data-testid={`hospitals-item-${i}`}
            >
              <div className="flex-1 min-w-0">
                <p className="text-[13.5px] font-semibold text-[#0F1A13] truncate">{h.name}</p>
                <p className="text-[11.5px] text-[#7A8A80] truncate">{h.address || '—'}</p>
              </div>
              {typeof h.distance_m === 'number' && (
                <span className="gt-pill gt-pill-light text-[10.5px] shrink-0">{formatDistance(h.distance_m)}</span>
              )}
              <ArrowRight className="w-3.5 h-3.5 text-[#7A8A80] shrink-0" />
            </a>
          );
        })}
      </div>
    </div>
  );
}
